import axios from "axios";
import { API_SERVICE_BACKEND } from "src/config";

// InboxKit routes are hosted on leadingly_testapp_backend
const BASE = `${API_SERVICE_BACKEND}/inboxkit`;

class InboxKitApi {
  // ── Account ───────────────────────────────────────────────────────────────

  getAccount = async () => {
    const res = await axios.get(`${BASE}/account`);
    return res.data;
  };

  // ── Domain availability ───────────────────────────────────────────────────

  checkDomainAvailability = async (domains) => {
    const res = await axios.post(`${BASE}/domains/check-availability`, { domains });
    return res.data;
  };

  checkDomainBanned = async (domain) => {
    const res = await axios.get(`${BASE}/domains/banned`, { params: { domain } });
    return res.data;
  };

  // ── Domain management ─────────────────────────────────────────────────────

  registerDomain = async (payload) => {
    const res = await axios.post(`${BASE}/domains/register`, payload);
    return res.data;
  };

  listDomains = async (params = {}) => {
    const res = await axios.post(`${BASE}/domains/list`, {
      page: params.page ?? 1,
      limit: params.limit ?? 10,
      userEmail: params?.email,
      ...(params.uids?.length && { uids: params.uids }),
      ...(params.keywords && { keywords: params.keywords }),
      ...(params.status?.length && { status: params.status }),
    });
    return res.data;
  };

  searchDomains = async (keyword) => {
    const res = await axios.post(`${BASE}/domains/search`, { keyword });
    return res.data;
  };

  getDomainDetails = async ({ uid, domain } = {}) => {
    const res = await axios.get(`${BASE}/domains/details`, {
      params: { ...(uid && { uid }), ...(domain && { domain }) },
    });
    return res.data;
  };

  getNameservers = async ({ uid, domain } = {}) => {
    const res = await axios.post(`${BASE}/domains/nameservers`, {
      ...(uid && { uid }),
      ...(domain && { domain }),
    });
    return res.data;
  };

  checkNameserverPropagation = async ({ uid, domain } = {}) => {
    const res = await axios.post(`${BASE}/domains/nameservers/check-propagation`, {
      ...(uid && { uid }),
      ...(domain && { domain }),
    });
    return res.data;
  };

  setDomainForwarding = async ({ uid, domain, forwarding_url } = {}) => {
    const res = await axios.post(`${BASE}/domains/forwarding`, {
      ...(uid && { uid }),
      ...(domain && { domain }),
      forwarding_url,
    });
    return res.data;
  };

  setCatchAll = async ({ uid, domain, catch_all_email } = {}) => {
    const res = await axios.post(`${BASE}/domains/catch-all`, {
      ...(uid && { uid }),
      ...(domain && { domain }),
      catch_all_email,
    });
    return res.data;
  };

  removeCatchAll = async ({ uid, domain } = {}) => {
    const res = await axios.delete(`${BASE}/domains/catch-all`, {
      data: { ...(uid && { uid }), ...(domain && { domain }) },
    });
    return res.data;
  };

  setDmarc = async ({ uid, domain, policy, rua } = {}) => {
    const res = await axios.post(`${BASE}/domains/dmarc`, {
      ...(uid && { uid }),
      ...(domain && { domain }),
      policy,
      ...(rua && { rua }),
    });
    return res.data;
  };

  removeDmarc = async ({ uid, domain } = {}) => {
    const res = await axios.delete(`${BASE}/domains/dmarc`, {
      data: { ...(uid && { uid }), ...(domain && { domain }) },
    });
    return res.data;
  };

  requestDomainRenewal = async ({ uid, domain, years = 1 } = {}) => {
    const res = await axios.post(`${BASE}/domains/renewals/request`, {
      ...(uid && { uid }),
      ...(domain && { domain }),
      years,
    });
    return res.data;
  };

  checkRenewalStatus = async (uid) => {
    const res = await axios.get(`${BASE}/domains/renewals/status`, { params: { uid } });
    return res.data;
  };

  // ── DNS management ────────────────────────────────────────────────────────

  getDnsRecords = async ({ domain, uid } = {}) => {
    const res = await axios.get(`${BASE}/dns/list`, {
      params: { ...(domain && { domain }), ...(uid && { uid }) },
    });
    return res.data;
  };

  // Fixed: was /dns/create
  addDnsRecord = async ({ domain, uid, type, name, value, ttl, priority } = {}) => {
    const res = await axios.post(`${BASE}/dns/add`, {
      ...(domain && { domain }),
      ...(uid && { uid }),
      type, name, value,
      ...(ttl && { ttl }),
      ...(priority !== undefined && { priority }),
    });
    return res.data;
  };

  updateDnsRecord = async ({ record_id, type, name, value, ttl, priority } = {}) => {
    const res = await axios.post(`${BASE}/dns/update`, {
      record_id,
      ...(type && { type }),
      ...(name && { name }),
      ...(value && { value }),
      ...(ttl && { ttl }),
      ...(priority !== undefined && { priority }),
    });
    return res.data;
  };

  deleteDnsRecord = async (record_id) => {
    const res = await axios.post(`${BASE}/dns/delete`, { record_id });
    return res.data;
  };

  verifyDnsRecords = async ({ domain, uid } = {}) => {
    const res = await axios.post(`${BASE}/dns/verify`, {
      ...(domain && { domain }),
      ...(uid && { uid }),
    });
    return res.data;
  };

  // ── Mailboxes ─────────────────────────────────────────────────────────────

  listMailboxes = async (params = {}) => {
    const res = await axios.post(`${BASE}/mailboxes/list`, {
      page: params.page ?? 1,
      limit: params.limit ?? 10,
      ...(params.keyword && { keyword: params.keyword }),
      ...(params.domain && { domain: params.domain }),
      ...(params.platform && { platform: params.platform }),
      ...(params.status && { status: params.status }),
    });
    return res.data;
  };

  searchMailboxes = async (keyword) => {
    const res = await axios.post(`${BASE}/prewarm/search-mailboxes`, { keyword });
    return res.data;
  };

  getMailboxDetails = async (uid) => {
    const res = await axios.get(`${BASE}/mailboxes/details`, { params: { uid } });
    return res.data;
  };

  checkMailboxAvailability = async (email) => {
    const res = await axios.get(`${BASE}/mailboxes/availability`, { params: { email } });
    return res.data;
  };

  buyMailboxes = async (payload) => {
    const res = await axios.post(`${BASE}/mailboxes/buy`, payload);
    return res.data;
  };

  // Fixed: was PATCH /mailboxes/:uid
  updateMailbox = async ({ uid, ...payload } = {}) => {
    const res = await axios.post(`${BASE}/mailboxes/update`, { uid, ...payload });
    return res.data;
  };

  updateMailboxSettings = async (uid, settings = {}) => {
    const res = await axios.patch(`${BASE}/mailboxes/${uid}/settings`, settings);
    return res.data;
  };

  checkMailboxStatus = async (mailbox_uids) => {
    const res = await axios.post(`${BASE}/mailboxes/status`, { mailbox_uids });
    return res.data;
  };

  getMailboxFailureReason = async (mailbox_uids) => {
    const res = await axios.post(`${BASE}/mailboxes/failure-reason`, { mailbox_uids });
    return res.data;
  };

  getMailboxCredentials = async (uid) => {
    const res = await axios.get(`${BASE}/mailboxes/show-credentials`, { params: { uid } });
    return res.data;
  };

  changeMailboxPassword = async ({ mailbox_uid, password } = {}) => {
    const res = await axios.post(`${BASE}/mailboxes/password`, { mailbox_uid, password });
    return res.data;
  };

  changeMailboxUsername = async ({ mailbox_uid, username } = {}) => {
    const res = await axios.post(`${BASE}/mailboxes/username`, { mailbox_uid, username });
    return res.data;
  };

  setMailboxSignature = async ({ mailbox_uids, signature } = {}) => {
    const res = await axios.post(`${BASE}/mailboxes/add-signature`, { mailbox_uids, signature });
    return res.data;
  };

  deleteMailboxSignature = async (mailbox_uids) => {
    const res = await axios.post(`${BASE}/mailboxes/delete-signature`, { mailbox_uids });
    return res.data;
  };

  cancelMailboxes = async (mailbox_uids) => {
    const res = await axios.post(`${BASE}/mailboxes/cancel`, { mailbox_uids });
    return res.data;
  };

  uncancelMailboxes = async (mailbox_uids) => {
    const res = await axios.post(`${BASE}/mailboxes/uncancel`, { mailbox_uids });
    return res.data;
  };

  resetupMailboxes = async (mailbox_uids) => {
    const res = await axios.post(`${BASE}/mailboxes/resetup`, { mailbox_uids });
    return res.data;
  };

  getResetupStatus = async (uid) => {
    const res = await axios.get(`${BASE}/mailboxes/resetup-status`, { params: { uid } });
    return res.data;
  };

  // ── Orders ────────────────────────────────────────────────────────────────

  placeOrder = async (payload) => {
    const res = await axios.post(`${BASE}/orders`, payload);
    return res.data;
  };

  getUserOrders = async ({ userEmail, page = 1, limit = 10 } = {}) => {
    const res = await axios.get(`${BASE}/orders/my`, {
      params: { userEmail, page, limit },
    });
    return res.data;
  };

  getOrderStatus = async (orderUid) => {
    const res = await axios.get(`${BASE}/orders/${orderUid}/status`);
    return res.data;
  };

  // Domain-only order flow (starts at mailbox_data, one doc per domain)
  placeDomainOnlyOrder = async (payload) => {
    const res = await axios.post(`${BASE}/orders/domain-only`, payload);
    return res.data;
  };

  getDomainOnlyOrderStatus = async (domainOrderId) => {
    const res = await axios.get(`${BASE}/orders/domain-only/order/${domainOrderId}/status`);
    return res.data;
  };

  getDomainOnlyOrderStepStatus = async (id) => {
    const res = await axios.get(`${BASE}/orders/domain-only/${id}/status`);
    return res.data;
  };

  // Free instant cancellation, only while the domain(s) are still inside their grace period
  cancelDomainOnlyOrder = async (domainOrderId, { userEmail, cancelReason } = {}) => {
    const res = await axios.post(`${BASE}/orders/domain-only/order/${domainOrderId}/cancel`, { userEmail, cancelReason });
    return res.data;
  };

  cancelDomainOnlyOrderStep = async (id, { userEmail, cancelReason } = {}) => {
    const res = await axios.post(`${BASE}/orders/domain-only/${id}/cancel`, { userEmail, cancelReason });
    return res.data;
  };

  buyPrewarmed = async (payload) => {
    const res = await axios.post(`${BASE}/prewarm/buy`, payload);
    return res.data;
  };

  // ── Warmup ────────────────────────────────────────────────────────────────

  listWarmup = async (params = {}) => {
    const res = await axios.post(`${BASE}/warmup/list`, {
      page: params.page ?? 1,
      limit: params.limit ?? 10,
      ...(params.status && { status: params.status }),
      ...(params.mailbox_uid && { mailbox_uid: params.mailbox_uid }),
      ...(params.userEmail && { userEmail: params.userEmail }),
    });
    return res.data;
  };

  // Fixed: was /warmup/status
  getWarmupStatus = async (uid) => {
    const res = await axios.get(`${BASE}/warmup/details`, { params: { uid } });
    return res.data;
  };

  // Fixed: was /warmup/analytics
  getWarmupAnalytics = async (uid, from, to) => {
    const res = await axios.get(`${BASE}/warmup/stats`, {
      params: { uid, ...(from && { from }), ...(to && { to }) },
    });
    return res.data;
  };

  getWarmupPricing = async () => {
    const res = await axios.get(`${BASE}/warmup/pricing`);
    return res.data;
  };

  startWarmup = async (mailbox_uids) => {
    const res = await axios.post(`${BASE}/warmup/start`, { mailbox_uids });
    return res.data;
  };

  // Fixed: was /warmup/stop (not in API)
  stopWarmup = async (mailbox_uids) => {
    const res = await axios.post(`${BASE}/warmup/cancel`, { mailbox_uids });
    return res.data;
  };

  pauseWarmup = async (mailbox_uids) => {
    const res = await axios.post(`${BASE}/warmup/pause`, { mailbox_uids });
    return res.data;
  };

  resumeWarmup = async (mailbox_uids) => {
    const res = await axios.post(`${BASE}/warmup/resume`, { mailbox_uids });
    return res.data;
  };

  // ── InfraGuard ────────────────────────────────────────────────────────────

  // Fixed: was /infraguard/setup
  setupInfraGuard = async (mailbox_uids) => {
    const res = await axios.post(`${BASE}/infra-guard/enable`, { mailbox_uids });
    return res.data;
  };

  cancelInfraGuard = async (mailbox_uids) => {
    const res = await axios.post(`${BASE}/infra-guard/cancel`, { mailbox_uids });
    return res.data;
  };

  getInfraGuardDetails = async ({ uid, domain } = {}) => {
    const res = await axios.get(`${BASE}/infra-guard/details`, {
      params: { ...(uid && { uid }), ...(domain && { domain }) },
    });
    return res.data;
  };

  // ── Admin (DB-backed) ─────────────────────────────────────────────────────

  listAdminOrders = async ({ page = 1, limit = 20, userEmail, status } = {}) => {
    const params = { page, limit };
    if (userEmail) params.userEmail = userEmail;
    if (status) params.status = status;
    const res = await axios.get(`${BASE}/admin/orders`, { params });
    return res.data;
  };

  processOrder = async (id) => {
    const res = await axios.post(`${BASE}/admin/orders/${id}/process`);
    return res.data;
  };

  // ── DFY lifecycle status ──────────────────────────────────────────────────

  getDfyMailboxStatus = async ({ domain, userEmail } = {}) => {
    const res = await axios.get(`${BASE}/dfy/status`, {
      params: {
        ...(domain    && { domain }),
        ...(userEmail && { userEmail }),
      },
    });
    return res.data;
  };

  // ── Consent ───────────────────────────────────────────────────────────────

  initiateConsentRequest = async ({ consent_url, mailbox_uid, username, domain, request_data } = {}) => {
    const res = await axios.post(`${BASE}/mailboxes/consent-request/initiate`, {
      consent_url,
      ...(mailbox_uid ? { mailbox_uid } : { username, domain }),
      ...(request_data && { request_data }),
    });
    return res.data;
  };

  listConsentRequests = async (params = {}) => {
    const res = await axios.get(`${BASE}/mailboxes/consent-requests`, {
      params: {
        ...(params.mailbox_uid && { mailbox_uid: params.mailbox_uid }),
        ...(params.status      && { status: params.status }),
        limit: params.limit ?? 20,
        page:  params.page  ?? 1,
      },
    });
    return res.data;
  };

  // Fixed: was /mailboxes/consent-requests/:uid
  getConsentRequestStatus = async (uid) => {
    const res = await axios.get(`${BASE}/mailboxes/consent-request/status/${uid}`);
    return res.data;
  };

  // ── Client ID ─────────────────────────────────────────────────────────────

  initiateClientIdRequest = async (mailbox_uids) => {
    const res = await axios.post(`${BASE}/mailboxes/client-id-request/initiate`, { mailbox_uids });
    return res.data;
  };

  getClientIdRequestStatus = async (requestId) => {
    const res = await axios.get(`${BASE}/mailboxes/client-id-request/status/${requestId}`);
    return res.data;
  };

  listClientIdRequests = async () => {
    const res = await axios.get(`${BASE}/mailboxes/client-id-requests`);
    return res.data;
  };

  // ── Tags ──────────────────────────────────────────────────────────────────

  listTags = async () => {
    const res = await axios.get(`${BASE}/tags`);
    return res.data;
  };

  createTag = async ({ name, color } = {}) => {
    const res = await axios.post(`${BASE}/tags`, { name, color });
    return res.data;
  };

  deleteTag = async (uid) => {
    const res = await axios.delete(`${BASE}/tags/${uid}`);
    return res.data;
  };

  assignTags = async ({ tag_ids, domain_uids, mailbox_uids } = {}) => {
    const res = await axios.post(`${BASE}/tags/assign`, {
      tag_ids,
      ...(domain_uids?.length  && { domain_uids }),
      ...(mailbox_uids?.length && { mailbox_uids }),
    });
    return res.data;
  };

  // ── Billing ───────────────────────────────────────────────────────────────

  getWalletBalance = async () => {
    const res = await axios.get(`${BASE}/billing/wallet`);
    return res.data;
  };

  getWalletTransactionLogs = async ({ page = 1, limit = 20 } = {}) => {
    const res = await axios.get(`${BASE}/billing/wallet/logs`, { params: { page, limit } });
    return res.data;
  };

  addCredits = async (amount) => {
    const res = await axios.post(`${BASE}/billing/wallet`, { amount });
    return res.data;
  };

  getPricingPlans = async () => {
    const res = await axios.get(`${BASE}/billing/pricing-plans`);
    return res.data;
  };

  getCurrentSubscription = async () => {
    const res = await axios.get(`${BASE}/billing/subscription`);
    return res.data;
  };

  getBillingPortal = async () => {
    const res = await axios.get(`${BASE}/billing/manage`);
    return res.data;
  };
}

export const inboxKitApi = new InboxKitApi();
